import { CareCircle } from '@/careCircle';
import { FieldType, MemberRole } from './enums';

import {
  CareProfileQuestion,
  CareProfileSubCategory,
  CareProfileCategory,
  CareProfileForm,
  Section,
  ProgressMetrics,
  SubSection,
  FormStatus,
} from '@/careProfile';

import { validationStringToMethod } from '@/utils';

type BuiltForm = {
  id: string;
  title: string;
  description?: string;
  fields: any;
  isComplete: boolean;
  prevFormID?: string;
  nextFormID?: string;
  progress?: ProgressMetrics;
};

// Given a subquestion and the answer of its parent, this checks whether the subquestion is relevant
export const isSubQuestionRelevant = (
  subQuestion: CareProfileQuestion,
  parentAnswer: any,
) => {
  if (parentAnswer === undefined || parentAnswer === null) return false;
  if (parentAnswer === true) parentAnswer = 'true';
  if (parentAnswer === false) parentAnswer = 'false';

  let conditionValue = subQuestion.conditionValue;

  switch (subQuestion.conditionOperator) {
    case 'equal':
      return parentAnswer === conditionValue;
    case 'not-equal':
      return parentAnswer !== conditionValue;
    case 'contains':
      return parentAnswer.includes(conditionValue as string);
    case 'in':
      return !!conditionValue && conditionValue.includes(parentAnswer);
    default:
      return true;
  }
};

export const listAllSubQuestions = (
  questions: CareProfileQuestion[],
): CareProfileQuestion[] => {
  if (!questions || questions.length === 0) return [];

  const subQuestions = questions.reduce(
    (acc: CareProfileQuestion[], question) => {
      return [...acc, ...(question.careProfileSubQuestions || [])];
    },
    [],
  );

  return [...questions, ...listAllSubQuestions(subQuestions || [])];
};

export const listAllSubQuestionsOfForm = (
  form: CareProfileForm,
): CareProfileQuestion[] => listAllSubQuestions(form.careProfileQuestions);

export const listAllQuestionsOfSubCategory = (
  subCategory: CareProfileSubCategory,
) =>
  subCategory?.careProfileForms?.reduce(
    (acc: CareProfileQuestion[], form) => [
      ...acc,
      ...listAllSubQuestionsOfForm(form),
    ],
    [],
  );

export const formFromCategory = (
  category: CareProfileCategory,
  formID: string,
): CareProfileForm | null => {
  let form: CareProfileForm | null = null;

  category.careProfileSubCategories.forEach((subc) => {
    subc.careProfileForms.forEach((f) => {
      if (f.id === formID) {
        form = f;
        return;
      }
    });

    if (form !== null) return;
  });

  return form;
};

export const answersFromMap = (
  careProfileId: string,
  answers: { [id: string]: string },
) => {
  return Object.keys(answers).map((questionId) => ({
    careProfileId,
    value: answers[questionId],
    careProfileQuestionId: questionId,
  }));
};

export const tableFromQuestion = (question: CareProfileQuestion) => {
  if (!question.fieldTable) {
    return {};
  }

  const splitTable = question.fieldTable?.split(';');

  return {
    title: splitTable[0],
    columns: splitTable[1].split(','),
    rows: splitTable[2].split(','),
  };
};

export const categoryByID = (categories: CareProfileCategory[], id?: string) =>
  categories?.find((category) => category.id === (id || 'care-plan'));

export const subQuestionsFromCategory = (category: CareProfileCategory) =>
  (category?.careProfileSubCategories || [])?.reduce(
    (acc: CareProfileQuestion[], subCategory) => [
      ...acc,
      ...listAllQuestionsOfSubCategory(subCategory),
    ],
    [],
  );

export const answersFromQuestions = (questions: CareProfileQuestion[]) => {
  const res: { [id: string]: string } = {};

  questions.forEach((q) => {
    if (q.answerValue !== undefined) {
      res[q.id] = q.answerValue;
    }
  });

  return res;
};

export const buildForm = (
  form: CareProfileForm,
  answers: { [id: string]: string },
) => {
  let fields: any = {};
  let order = 0;

  const assignField = (question: CareProfileQuestion) => {
    const table = tableFromQuestion(question);

    order += 10;

    const mapField = (question: CareProfileQuestion) => {
      const labels: { [key: string]: string } = {};
      if (question.fieldLabels) {
        question.fieldLabels.split(';').forEach((label) => {
          const labelSplit = label.split(':');
          labels[labelSplit[0]] = labelSplit[1];
        });
      }
      return {
        id: question.id,
        label: question.label,
        supportText: question?.supportText,
        isRequired: question.isRequired,
        placeholder: question.placeholder,
        tooltip: question.tooltip,
        validation: validationStringToMethod(question.validation as string),
        field: {
          labels,
          table,
          type: question.fieldType,
          // size: 'sm', // TODO: check this?
          limits: {
            min: parseInt(question.fieldLimits?.split(',')?.[0]),
            max: parseInt(question.fieldLimits?.split(',')?.[1]),
          },
          options: question.fieldOptions
            ?.map((option: any) => ({
              value: option.id,
              label: option.label,
              order: option.order,
            }))
            .sort((a, b) => a.order - b.order),
          // TODO: check this .sort((a: any, b: any) => a.label.localeCompare(b.label)),
          isCustom: question.fieldIsCustom,
        },
      };
    };

    const mappedField = mapField(question);

    fields[question.id] = {
      ...mappedField,
      order,
      field: {
        ...mappedField.field,
        subfields:
          question.fieldType === FieldType.QuestionTable
            ? question.careProfileSubQuestions
                ?.slice()
                .sort((a, b) => a.order - b.order)
                .map(mapField)
            : undefined,
      },
    };
  };

  const questions = form.careProfileQuestions || [];

  questions?.forEach((question) => {
    if (question.conditionQuestionKey) {
      const conditionAnswer = answers?.[question.conditionQuestionKey];
      if (conditionAnswer !== undefined) {
        if (!isSubQuestionRelevant(question, conditionAnswer)) {
          console.log(
            'condition for question is not met, hiding it',
            question.id,
            question.conditionQuestionKey,
          );

          return;
        }
      }
    }

    assignField(question);

    const createSubQuestions = (q: CareProfileQuestion, val: any) => {
      if (q.fieldType === FieldType.QuestionTable) return;

      [...(q.careProfileSubQuestions || [])]
        ?.sort((a, b) => a.order - b.order)
        ?.filter((subQ) => isSubQuestionRelevant(subQ, val))
        ?.forEach((subQ) => {
          assignField(subQ);

          createSubQuestions(subQ, answers?.[subQ.id]);
        });
    };

    createSubQuestions(question, answers?.[question.id]);
  });

  return {
    id: form.id,
    title: form.title,
    description: form.description,
    progress: form.progress,
    fields,
    isComplete: questions?.every(
      (question) =>
        (answers?.[question.id] !== undefined ||
          question.isRequired === false) &&
        answers?.[question.id] !== '',
    ),
  };
};

export const buildForms = (
  category: CareProfileCategory,
  answers: { [questionID: string]: string },
) => {
  const res: BuiltForm[] = [];

  (category?.careProfileSubCategories || []).forEach((subCategory) => {
    subCategory.careProfileForms.forEach((form: any) => {
      // if the form has no fields, skip it
      const built = buildForm(form, answers);
      if (Object.keys(built.fields).length === 0) {
        return;
      }

      res.push(built);
    });
  });

  // Set prevFormId and nextFormId for each form
  res.forEach((form, index) => {
    form.prevFormID = index > 0 ? res[index - 1].id : undefined;
    form.nextFormID = index < res.length - 1 ? res[index + 1].id : undefined;
  });

  return res;
};

export const buildSections = (
  category: CareProfileCategory,
  builtForms: BuiltForm[],
): Section[] => {
  const builtFormsMap: { [id: string]: BuiltForm } = {};
  builtForms.forEach((form) => {
    builtFormsMap[form.id] = form;
  });

  let output: Section[] = [];

  for (const careProfileSubCategory of category.careProfileSubCategories ||
    []) {
    let subSections: SubSection[] = [];
    for (const form of careProfileSubCategory.careProfileForms) {
      const builtForm = builtFormsMap[form.id];
      if (!builtForm) {
        console.log('form not found in builtFormsMap, will skip', form.id);
        continue;
      }

      subSections.push({
        id: form.id,
        title: form.title,
        isComplete: builtForm?.isComplete || false,
        to: `/${careProfileSubCategory.careProfileCategoryId}/${form.id}`,
        sectionStatus: builtForm?.progress?.isComplete
          ? 'complete'
          : builtForm?.progress?.isInProgress
            ? 'inProgress'
            : 'notStarted',
      });
    }

    if (subSections.length > 0) {
      output.push({
        id: careProfileSubCategory.id,
        title: careProfileSubCategory.title,
        firstFormID: careProfileSubCategory.firstFormID,
        finalFormID: careProfileSubCategory.finalFormID,
        subSections,
        sectionStatus: subSections.every(
          (subSection) => subSection.sectionStatus === 'complete',
        )
          ? 'complete'
          : subSections.some(
                (subSection) => subSection.sectionStatus === 'inProgress',
              )
            ? 'inProgress'
            : 'notStarted',
      });
    }
  }

  return output;
};

// Methods

const SELECT_TYPES = new Set<string>([
  FieldType.Select,
  FieldType.SelectCustom,
  FieldType.RadioGroup,
  FieldType.ToggleSelect,
]);

const MULTI_SELECT_TYPES = new Set<string>([
  FieldType.MultiSelect,
  FieldType.MultiSelectChip,
  FieldType.MultiSelectBox,
  FieldType.MultiSelectCustom,
]);

export const formatAnswer = (rawValue: any, field: any): string => {
  if (
    rawValue === undefined ||
    rawValue === null ||
    rawValue === '' ||
    rawValue === '[]'
  ) {
    return '—';
  }

  const type = field?.field?.type;
  const options: { value: string; label: string }[] | undefined =
    field?.field?.options;

  // Boolean / Switch
  if (type === FieldType.Boolean || type === FieldType.Switch) {
    const v = String(rawValue).toLowerCase();
    if (v === 'true' || v === '1' || v === 'yes') return 'Yes';
    if (v === 'false' || v === '0' || v === 'no') return 'No';
    return String(rawValue);
  }

  // Date
  if (type === FieldType.DatePicker) {
    const d = new Date(rawValue);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB');
    }
    return String(rawValue);
  }

  // Single select / radio / toggle
  if (type && SELECT_TYPES.has(type)) {
    const match = options?.find((o) => o.value === rawValue);
    return match?.label ?? String(rawValue);
  }

  // Multi-select (array stored as JSON string or comma-separated)
  if (type && MULTI_SELECT_TYPES.has(type)) {
    let arr: any[] = [];
    if (Array.isArray(rawValue)) {
      arr = rawValue;
    } else {
      try {
        const parsed = JSON.parse(String(rawValue));
        arr = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        arr = String(rawValue)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    if (arr.length === 0) return '—';
    return arr
      .map((v) => options?.find((o) => o.value === v)?.label ?? String(v))
      .join(', ');
  }

  // Default — strip wrapping JSON noise like { true: '' } or weird stringified objects
  if (typeof rawValue === 'object') {
    return JSON.stringify(rawValue);
  }
  return String(rawValue);
};

export const combineFields = (category?: any, forms?: any) => {
  const merged: Record<string, any> = {};
  let order = 0;

  // Walk the raw category (every subCategory → form → question) so we
  // surface every editable field, including ones whose conditional gates
  // aren't currently met. This is intentionally broader than `data.forms`
  // (which `buildForms` filters by active conditional state).
  const questionToField = (q: any) => {
    const labels: Record<string, string> = {};
    if (q.fieldLabels) {
      q.fieldLabels.split(';').forEach((label: string) => {
        const [k, v] = label.split(':');
        if (k) labels[k] = v;
      });
    }
    const table = q.fieldTable
      ? (() => {
          const parts = q.fieldTable.split(';');
          return {
            title: parts[0],
            columns: parts[1]?.split(',') ?? [],
            rows: parts[2]?.split(',') ?? [],
          };
        })()
      : undefined;
    const limits = q.fieldLimits
      ? {
          min: parseInt(q.fieldLimits.split(',')[0]),
          max: parseInt(q.fieldLimits.split(',')[1]),
        }
      : undefined;
    const options = q.fieldOptions
      ?.map((o: any) => ({
        value: o.id,
        label: o.label,
        order: o.order,
      }))
      .sort((a: any, b: any) => a.order - b.order);
    order += 10;
    return {
      id: q.id,
      label: q.label,
      placeholder: q.placeholder,
      isRequired: q.isRequired,
      tooltip: q.tooltip,
      description: q.description,
      order,
      field: {
        labels,
        table,
        type: q.fieldType,
        limits,
        options,
        isCustom: q.fieldIsCustom,
      },
    };
  };

  const subCategories = category?.careProfileSubCategories ?? [];
  subCategories.forEach((subCat: any) => {
    (subCat.careProfileForms ?? []).forEach((form: any) => {
      const collectQuestions = (questions: any[] = []) => {
        questions.forEach((q: any) => {
          if (q?.id && q?.fieldType) {
            merged[q.id] = questionToField(q);
          }
          if (q?.careProfileSubQuestions?.length) {
            collectQuestions(q.careProfileSubQuestions);
          }
        });
      };
      collectQuestions(form.careProfileQuestions);
    });
  });

  // Fallback: if the raw category isn't populated (defensive), at least
  // merge whatever buildForms produced.
  if (Object.keys(merged).length === 0) {
    (forms ?? []).forEach((form: any) => {
      if (form?.fields) Object.assign(merged, form.fields);
    });
  }

  return merged;
};

export const computeAge = (dobStr?: string): number | undefined => {
  if (!dobStr) return undefined;
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return undefined;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age >= 0 ? age : undefined;
};

export const parseConditions = (raw?: string): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed))
      return parsed
        .map(String)
        .map((s) => s.trim())
        .filter(Boolean);
  } catch {
    // fall through
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

export const initialsFor = (firstName?: string, lastName?: string) =>
  `${(firstName?.[0] ?? '').toUpperCase()}${(lastName?.[0] ?? '').toUpperCase()}`;

export const computeFormStatus = (sections?: Section[]): FormStatus => {
  if (!sections || sections.length === 0) return 'notStarted';
  const statuses = sections.map((s) => s.sectionStatus);
  if (statuses.every((s) => s === 'complete')) return 'complete';
  if (statuses.some((s) => s === 'complete' || s === 'inProgress'))
    return 'inProgress';
  return 'notStarted';
};

export const combineSectionFields = (forms: any, subSectionIds: string[]) => {
  const merged: Record<string, any> = {};
  (forms ?? []).forEach((form: any) => {
    if (subSectionIds.find((id) => id === form?.id) && form?.fields) {
      Object.assign(merged, form.fields);
    }
  });
  return merged;
};

interface SectionViewRowProps {
  id: string;
  label: string;
  answer: string;
}

export const computeSectionRows = (
  subSections: Array<{
    id: string;
    title: string;
  }>,
  forms: any,
  answers: any,
) => {
  const out: SectionViewRowProps[] = [];
  subSections.forEach((subsec) => {
    const form = (forms ?? []).find((f: any) => f?.id === subsec.id);
    if (!form?.fields) return;
    Object.keys(form.fields).forEach((key: any) => {
      const field = form.fields[key];
      const fieldId = field.id ?? field?.field?.id;
      if (!fieldId) return;
      out.push({
        id: fieldId,
        label: field.label ?? '',
        answer: formatAnswer(answers?.[fieldId], field),
      });
    });
  });
  return out;
};

export const dobDisplay = (dob: any, age?: any) => {
  if (!dob) return '';
  const d = new Date(dob);
  const formatted = Number.isNaN(d.getTime())
    ? dob
    : d.toLocaleDateString('en-GB');
  return age !== undefined ? `${formatted} · ${age}` : formatted;
};

// Care Circle Methods

export const careRecipientFromCareCircle = (careCircle?: CareCircle) => {
  const firstName = careCircle?.careRecipientAccount?.firstName ?? '';
  const lastName = careCircle?.careRecipientAccount?.lastName ?? '';
  return {
    firstName,
    lastName,
    initials: initialsFor(firstName, lastName) || '??',
  };
};

export const otherCarerMembersFromCareCircle = (careCircle?: CareCircle) => {
  return (careCircle?.careCircleMembers ?? []).filter(
    (m) =>
      m.account?.id !== careCircle?.primaryCaregiverAccount?.id &&
      !m.roles?.some((r) => r.name === MemberRole.CareRecipient),
  );
};
